// Advanced Cache Service
// Handles intelligent caching strategies, API caching, and cache management

export interface CacheStrategy {
  name: 'CacheFirst' | 'NetworkFirst' | 'StaleWhileRevalidate' | 'NetworkOnly' | 'CacheOnly';
  options?: {
    cacheName?: string;
    maxAge?: number;
    maxEntries?: number;
    networkTimeout?: number;
    cacheableResponse?: {
      statuses: number[];
      headers?: Record<string, string>;
    };
  };
}

export interface CacheConfig {
  name: string;
  strategy: CacheStrategy;
  patterns: string[];
  maxAge?: number;
  maxEntries?: number;
  networkTimeout?: number;
}

export interface CacheStats {
  name: string;
  // size: number; // Removed for performance - reading blobs is expensive
  entries: number;
  lastUpdated: Date;
  hitRate: number;
  missRate: number;
  error?: string; // Added for error handling
}

export interface APICacheConfig {
  endpoint: string;
  strategy: CacheStrategy;
  maxAge: number;
  maxEntries: number;
  invalidateOn?: string[];
  tags?: string[];
}

export class AdvancedCacheService {
  private cacheConfigs: Map<string, CacheConfig> = new Map();
  private apiCacheConfigs: Map<string, APICacheConfig> = new Map();
  private cacheStats: Map<string, CacheStats> = new Map();
  private isSupported: boolean;

  constructor() {
    this.isSupported = 'caches' in window && 'serviceWorker' in navigator;
    this.initDefaultCaches();
  }

  /**
   * Initialize default cache configurations
   */
  private initDefaultCaches(): void {
    if (!this.isSupported) return;

    // Static assets cache
    this.addCacheConfig({
      name: 'static-assets',
      strategy: {
        name: 'CacheFirst',
        options: {
          cacheName: 'static-assets-v1',
          maxAge: 7 * 24 * 60 * 60 * 1000, // 7 days
          maxEntries: 100
        }
      },
      patterns: [
        '/assets/**/*',
        '**/*.css',
        '**/*.js',
        '**/*.png',
        '**/*.jpg',
        '**/*.jpeg',
        '**/*.gif',
        '**/*.svg',
        '**/*.woff',
        '**/*.woff2',
        '**/*.ttf',
        '**/*.eot'
      ]
    });

    // API responses cache
    this.addCacheConfig({
      name: 'api-responses',
      strategy: {
        name: 'NetworkFirst',
        options: {
          cacheName: 'api-responses-v1',
          maxAge: 5 * 60 * 1000, // 5 minutes
          maxEntries: 50,
          networkTimeout: 3000
        }
      },
      patterns: [
        '/api/**/*'
      ]
    });

    // User data cache
    this.addCacheConfig({
      name: 'user-data',
      strategy: {
        name: 'StaleWhileRevalidate',
        options: {
          cacheName: 'user-data-v1',
          maxAge: 10 * 60 * 1000, // 10 minutes
          maxEntries: 20
        }
      },
      patterns: [
        '/api/user/**/*',
        '/api/profile/**/*',
        '/api/settings/**/*'
      ]
    });
  }

  /**
   * Add a new cache configuration
   */
  addCacheConfig(config: CacheConfig): void {
    this.cacheConfigs.set(config.name, config);
    console.log(`Cache config added: ${config.name}`);
  }

  /**
   * Add API cache configuration
   */
  addAPICacheConfig(config: APICacheConfig): void {
    this.apiCacheConfigs.set(config.endpoint, config);
    console.log(`API cache config added: ${config.endpoint}`);
  }

  /**
   * Get cache configuration by name
   */
  getCacheConfig(name: string): CacheConfig | undefined {
    return this.cacheConfigs.get(name);
  }

  /**
   * Get API cache configuration by endpoint
   */
  getAPICacheConfig(endpoint: string): APICacheConfig | undefined {
    return this.apiCacheConfigs.get(endpoint);
  }

  /**
   * Cache a request with the appropriate strategy
   */
  async cacheRequest(request: Request, config?: CacheConfig): Promise<Response> {
    if (!this.isSupported) {
      throw new Error('Caching is not supported in this browser');
    }

    const cacheName = config?.strategy.options?.cacheName || 'default-cache';
    const strategy = config?.strategy.name || 'NetworkFirst';

    try {
      switch (strategy) {
        case 'CacheFirst':
          return await this.cacheFirstStrategy(request, cacheName);
        case 'NetworkFirst':
          return await this.networkFirstStrategy(request, cacheName, config?.strategy.options?.networkTimeout);
        case 'StaleWhileRevalidate':
          return await this.staleWhileRevalidateStrategy(request, cacheName);
        case 'NetworkOnly':
          return await this.networkOnlyStrategy(request);
        case 'CacheOnly':
          return await this.cacheOnlyStrategy(request, cacheName);
        default:
          return await this.networkFirstStrategy(request, cacheName);
      }
    } catch (error) {
      console.error('Cache strategy failed:', error);
      // Fallback to network request
      return await fetch(request);
    }
  }

  /**
   * Cache First Strategy
   */
  private async cacheFirstStrategy(request: Request, cacheName: string): Promise<Response> {
    const cache = await caches.open(cacheName);
    const cachedResponse = await cache.match(request);
    
    if (cachedResponse) {
      this.updateCacheStats(cacheName, 'hit');
      return cachedResponse;
    }

    const networkResponse = await fetch(request);
    if (networkResponse.ok) {
      await cache.put(request, networkResponse.clone());
      this.updateCacheStats(cacheName, 'miss');
    }
    
    return networkResponse;
  }

  /**
   * Network First Strategy
   */
  private async networkFirstStrategy(request: Request, cacheName: string, timeout?: number): Promise<Response> {
    const cache = await caches.open(cacheName);
    
    try {
      const controller = new AbortController();
      const timeoutId = timeout ? setTimeout(() => controller.abort(), timeout) : null;
      
      const networkResponse = await fetch(request, { signal: controller.signal });
      
      if (timeoutId) clearTimeout(timeoutId);
      
      if (networkResponse.ok) {
        await cache.put(request, networkResponse.clone());
        this.updateCacheStats(cacheName, 'hit');
        return networkResponse;
      }
    } catch (error) {
      console.log('Network request failed, trying cache:', error);
    }

    // Fallback to cache
    const cachedResponse = await cache.match(request);
    if (cachedResponse) {
      this.updateCacheStats(cacheName, 'hit');
      return cachedResponse;
    }

    // If both network and cache fail, return error response
    this.updateCacheStats(cacheName, 'miss');
    return new Response('Network error', { status: 503 });
  }

  /**
   * Stale While Revalidate Strategy
   */
  private async staleWhileRevalidateStrategy(request: Request, cacheName: string): Promise<Response> {
    const cache = await caches.open(cacheName);
    const cachedResponse = await cache.match(request);
    
    // Return cached response immediately if available
    if (cachedResponse) {
      this.updateCacheStats(cacheName, 'hit');
      
      // Update cache in background
      this.updateCacheInBackground(request, cache, cacheName);
      
      return cachedResponse;
    }

    // No cache, fetch from network
    const networkResponse = await fetch(request);
    if (networkResponse.ok) {
      await cache.put(request, networkResponse.clone());
      this.updateCacheStats(cacheName, 'miss');
    }
    
    return networkResponse;
  }

  /**
   * Network Only Strategy
   */
  private async networkOnlyStrategy(request: Request): Promise<Response> {
    return await fetch(request);
  }

  /**
   * Cache Only Strategy
   */
  private async cacheOnlyStrategy(request: Request, cacheName: string): Promise<Response> {
    const cache = await caches.open(cacheName);
    const cachedResponse = await cache.match(request);
    
    if (cachedResponse) {
      this.updateCacheStats(cacheName, 'hit');
      return cachedResponse;
    }
    
    this.updateCacheStats(cacheName, 'miss');
    throw new Error('No cached response available');
  }

  /**
   * Update cache in background
   */
  private async updateCacheInBackground(request: Request, cache: Cache, cacheName: string): Promise<void> {
    try {
      const networkResponse = await fetch(request);
      if (networkResponse.ok) {
        await cache.put(request, networkResponse.clone());
        this.updateCacheStats(cacheName, 'update');
      }
    } catch (error) {
      console.log('Background cache update failed:', error);
    }
  }

  /**
   * Cache API response with intelligent invalidation
   */
  async cacheAPIResponse(endpoint: string, response: Response, data: any): Promise<void> {
    if (!this.isSupported) return;

    const config = this.apiCacheConfigs.get(endpoint);
    if (!config) return;

    const cache = await caches.open(config.strategy.options?.cacheName || 'api-cache');
    const request = new Request(endpoint);
    
    // Add metadata to response
    const metadata = {
      timestamp: Date.now(),
      maxAge: config.maxAge,
      tags: config.tags || [],
      invalidateOn: config.invalidateOn || []
    };

    const responseWithMetadata = new Response(JSON.stringify({
      data,
      _metadata: metadata
    }), {
      headers: {
        'Content-Type': 'application/json',
        'X-Cache-Timestamp': metadata.timestamp.toString(),
        'X-Cache-MaxAge': metadata.maxAge.toString()
      }
    });

    await cache.put(request, responseWithMetadata);
    this.updateCacheStats('api-cache', 'hit');
  }

  /**
   * Get cached API response
   */
  async getCachedAPIResponse(endpoint: string): Promise<any | null> {
    if (!this.isSupported) return null;

    const config = this.apiCacheConfigs.get(endpoint);
    if (!config) return null;

    const cache = await caches.open(config.strategy.options?.cacheName || 'api-cache');
    const request = new Request(endpoint);
    const cachedResponse = await cache.match(request);

    if (!cachedResponse) return null;

    try {
      const responseData = await cachedResponse.json();
      const metadata = responseData._metadata;
      
      // Check if cache is still valid
      if (Date.now() - metadata.timestamp > config.maxAge) {
        // Cache expired, remove it
        await cache.delete(request);
        return null;
      }

      this.updateCacheStats('api-cache', 'hit');
      return responseData.data;
    } catch (error) {
      console.error('Error parsing cached response:', error);
      return null;
    }
  }

  /**
   * Invalidate cache by tags
   */
  async invalidateCacheByTags(tags: string[]): Promise<void> {
    if (!this.isSupported) return;

    for (const [name, config] of Array.from(this.cacheConfigs.entries())) {
      const cache = await caches.open(config.strategy.options?.cacheName || name);
      const requests = await cache.keys();
      
      for (const request of requests) {
        try {
          const response = await cache.match(request);
          if (response) {
            const responseData = await response.json();
            const metadata = responseData._metadata;
            
            if (metadata?.tags && tags.some(tag => metadata.tags.includes(tag))) {
              await cache.delete(request);
              console.log(`Invalidated cached request: ${request.url}`);
            }
          }
        } catch (error) {
          // Skip non-JSON responses
        }
      }
    }
  }

  /**
   * Clear all caches
   */
  async clearAllCaches(): Promise<void> {
    if (!this.isSupported) return;

    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(name => caches.delete(name)));
    
    console.log('All caches cleared');
  }

  /**
   * Clear specific cache
   */
  async clearCache(name: string): Promise<void> {
    if (!this.isSupported) return;

    const deleted = await caches.delete(name);
    if (deleted) {
      console.log(`Cache cleared: ${name}`);
    } else {
      console.log(`Cache not found: ${name}`);
    }
  }

  /**
   * Get cache statistics (optimized for performance)
   */
  async getCacheStats(): Promise<CacheStats[]> {
    if (!this.isSupported) return [];

    const stats: CacheStats[] = [];
    
    for (const [name, config] of Array.from(this.cacheConfigs.entries())) {
      try {
        const cache = await caches.open(config.strategy.options?.cacheName || name);
        const keys = await cache.keys();
        
        // Only count entries - skip size calculation for performance
        // This prevents reading large blobs (images/videos) into memory

        const cacheStats = this.cacheStats.get(name) || {
          name,
          size: 0,
          entries: 0,
          lastUpdated: new Date(),
          hitRate: 0,
          missRate: 0
        };

        stats.push({
          ...cacheStats,
          // size: 'N/A' // Removed for performance
          entries: keys.length
        });
      } catch (error) {
        console.error(`Failed to get stats for cache ${name}:`, error);
        stats.push({
          name,
          entries: 0,
          // size: 0,
          lastUpdated: new Date(),
          hitRate: 0,
          missRate: 0,
          error: error.message
        });
      }
    }

    return stats;
  }

  /**
   * Update cache statistics
   */
  private updateCacheStats(cacheName: string, type: 'hit' | 'miss' | 'update'): void {
    const stats = this.cacheStats.get(cacheName) || {
      name: cacheName,
      // size: 0, // Removed for performance
      entries: 0,
      lastUpdated: new Date(),
      hitRate: 0,
      missRate: 0
    };

    if (type === 'hit') {
      stats.hitRate++;
    } else if (type === 'miss') {
      stats.missRate++;
    }

    stats.lastUpdated = new Date();
    this.cacheStats.set(cacheName, stats);
  }

  /**
   * Preload important resources
   */
  async preloadResources(urls: string[]): Promise<void> {
    if (!this.isSupported) return;

    const cache = await caches.open('preload-cache');
    
    for (const url of urls) {
      try {
        const response = await fetch(url);
        if (response.ok) {
          await cache.put(url, response);
          console.log(`Preloaded resource: ${url}`);
        }
      } catch (error) {
        console.error(`Failed to preload resource: ${url}`, error);
      }
    }
  }

  /**
   * Get cache size information (optimized for performance)
   */
  async getCacheSizeInfo(): Promise<{ totalSize: string; totalEntries: number; cacheCount: number }> {
    if (!this.isSupported) {
      return { totalSize: 'N/A', totalEntries: 0, cacheCount: 0 };
    }

    let totalEntries = 0;
    const cacheNames = await caches.keys();

    for (const name of cacheNames) {
      try {
        const cache = await caches.open(name);
        const keys = await cache.keys();
        totalEntries += keys.length;
      } catch (error) {
        console.warn(`Failed to count entries for cache ${name}:`, error);
      }
    }

    return {
      totalSize: 'N/A (Performance optimized)', // Size calculation removed for performance
      totalEntries,
      cacheCount: cacheNames.length
    };
  }

  /**
   * Check if caching is supported
   */
  isCachingSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Get all cache names
   */
  async getCacheNames(): Promise<string[]> {
    if (!this.isSupported) return [];
    return await caches.keys();
  }
}

// Create singleton instance
export const advancedCacheService = new AdvancedCacheService();
